import { ChangeEvent, useEffect, useState, FormEvent, useContext } from 'react';
import { useSelector } from 'react-redux';

import { getQuotationId } from 'app/store/data/quotation';
import { DictService, DictionaryEntry } from 'app/services/dict.service';
import { QuotationService } from 'app/services/quotation.service';

import { MubiInstallmentsContext } from 'app/components/MubiInstallmentsRadioOption/useMubiInstallments.context';
import { HiProPaymentService } from 'app/services/hiProPayment.services';
import { HiProConsentWithTextDto } from 'app/shared/interfaces/HiProApplicationResponse.interface';
import {
  ErrorConfig,
  ErrorDecision,
  getMubiInstalmentError,
} from 'app/shared/helpers/getMubiInstalmentError';
import { useNotification } from 'app/shared/hooks/useNotification';
import { selectSelectedOfferId } from 'app/store/data/selectedOffer';
import { getOffer } from 'app/store/data/quotationResult';
import { useForm } from 'app/shared/hooks/useForm';
import { useCustomEvents } from 'app/shared/hooks/useCustomEvents';
import { setItem } from 'app/services/storage.service';

import {
  MessageTypeEnum,
  LabelEnum,
  FormsEnum,
  ConsentsEnum,
  CustomEventsNameEnum,
  InstallmentsEnum,
} from 'app/shared/enums';
import { useMubiInstallments } from 'app/components/MubiInstallmentsRadioOption/useMubiInstallments';
import {
  ApplicationDataFormProps,
  IdCardData,
  InsurantData,
} from './ApplicationDataForm.interfaces';
import { hideMarketingConsentCode } from './useConsentHiPro';

export function useApplicationDataForm({
  initialize,
  handleSubmit,
  change,
}: ApplicationDataFormProps) {
  const {
    hiProConsents,
    hiProToken,
    closeModal,
    setErrorOnModal,
    mubiInstallmentsDataLayerEvent,
    setActiveStep,
  } = useContext(MubiInstallmentsContext);
  const { triggerEvent } = useCustomEvents();

  const notify = useNotification();
  const offerId = useSelector(selectSelectedOfferId);
  const offer = useSelector(getOffer(offerId));
  const { formValues } = useForm(FormsEnum.MubiInstallmentsDataForm);
  const { clearTokenState } = useMubiInstallments(offer);

  const [isLoading, setIsLoading] = useState(false);

  const [countries, setCountries] = useState<DictionaryEntry[]>();
  const [
    idCardIndefinitelyValid,
    setIdCardIndefinitelyValid,
  ] = useState<boolean>(false);

  const [insurantData, setInsurantData] = useState<InsurantData>();

  const quotationId = useSelector(getQuotationId);

  useEffect(() => {
    fetchCountries();
    initialize({ birthCountry: 'PL' });
    getInsurantData(quotationId);

    mubiInstallmentsDataLayerEvent(
      InstallmentsEnum.EventModalStep,
      InstallmentsEnum.EventStepNamePersonalData
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async (idCardData: IdCardData) => {
    if (!hiProToken) {
      return;
    }
    const {
      idCard,
      idCardExpiryDate,
      idCardIssueDate,
      indefiniteIdCard = false,
      birthCountry,
    } = idCardData;

    setIsLoading(true);

    const consents = (hiProConsents as Array<HiProConsentWithTextDto>).map(
      (consent) => {
        if (consent.code === hideMarketingConsentCode) {
          return {
            code: consent.code,
            granted: idCardData[ConsentsEnum.ACCEPTMARKETINGAUTOMATEDCONTACT],
          };
        }

        return {
          code: consent.code,
          granted: idCardData[consent.code],
        };
      }
    );

    const data = {
      consents,
      idCard,
      idCardExpiryDate,
      idCardIssueDate,
      indefiniteIdCard,
      birthCountry,
    };

    await HiProPaymentService.beginHiProPayment({
      token: hiProToken,
      data,
    })
      .then(() => {
        setActiveStep(2);
      })
      .catch(({ response }) => {
        const hiProError =
          response?.data &&
          getMubiInstalmentError(
            response.data.errorKey || response.data.message
          );

        if (!hiProError) {
          notify(MessageTypeEnum.Danger, LabelEnum.ServerError);
        } else if (hiProError === ErrorDecision.NegativeDecision) {
          setActiveStep(7);
          setItem(`hiPro_verification_decision_${hiProToken}`, {
            verificationDecision: true,
          });
        } else {
          closeModal();
          setErrorOnModal(hiProError as ErrorConfig);
          clearTokenState();
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const onFormSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    mubiInstallmentsDataLayerEvent(
      InstallmentsEnum.EventNameConsentNextStep,
      InstallmentsEnum.EventStepNamePersonalData
    );

    return handleSubmit(onSubmit)();
  };

  const getInsurantData = async (quotationId: string) => {
    if (!quotationId) return;

    await QuotationService.getQuotationById(quotationId).then(
      ({
        data: {
          insurant: {
            name,
            lastName,
            peselNumber,
            homeAddress: {
              streetName,
              streetNumber,
              apartmentNumber,
              zipCode,
              city,
            },
          },
          emailAddress,
          phoneNumber,
        },
      }) => {
        setInsurantData({
          name,
          lastName,
          peselNumber,
          streetName,
          streetNumber,
          apartmentNumber,
          zipCode,
          city,
          emailAddress,
          phoneNumber,
        });
      }
    );
  };

  const fetchCountries = () => {
    DictService.getDictionary('countries').then(({ data: countries }) => {
      setCountries(countries);
    });
  };

  const handleIdCardIndefinitelyValid = (
    value: ChangeEvent<HTMLInputElement>
  ) => {
    change('idCardExpiryDate', '');
    triggerEvent(CustomEventsNameEnum.ClearInputValue);
    setIdCardIndefinitelyValid(!!value);
  };

  const handleIdCardIssueDateChange = (
    value: ChangeEvent<HTMLInputElement>
  ) => {
    if (idCardIndefinitelyValid) {
      return;
    }
    const dateDetails = String(value)
      .split('-')
      .map((s) => parseInt(s));

    const selectedData = new Date(
      dateDetails[0],
      dateDetails[1] - 1,
      dateDetails[2]
    );
    const rangeData = new Date(2021, 7, 3);

    if (selectedData < rangeData) {
      const idCardExpiryDate = new Date(
        dateDetails[0] + 10,
        dateDetails[1] - 1,
        dateDetails[2] + 1
      );

      const idCardExpiryDateFormat = idCardExpiryDate
        .toISOString()
        .split('T')[0];

      change('idCardExpiryDate', idCardExpiryDateFormat);
    }
  };

  const getStartDate = (field: string) => {
    const currentDate = formValues?.[field];

    if (!currentDate) return new Date();

    const dateDetails = String(currentDate)
      .split('-')
      .map((s) => parseInt(s));

    const currentParsedDate = new Date(
      dateDetails[0],
      dateDetails[1] - 1,
      dateDetails[2]
    );

    return currentParsedDate;
  };

  return {
    insurantData,
    countries,
    hiProConsents,
    idCardIndefinitelyValid,
    onFormSubmit,
    handleIdCardIndefinitelyValid,
    isLoading,
    handleIdCardIssueDateChange,
    getStartDate,
  };
}
